// Runtime companion to the build-time guard in scripts/check-env.ts.
//
// The build gate is the primary defence, but it is skippable in ways this
// codebase does not control: a host overriding the build command, a fork, a
// direct `vite build`. When a bundle is produced anyway, the components still
// need to tell "configured" from "someone copied .env.example", so this repeats
// the placeholder rejection at runtime.
//
// The gate compares against the exact placeholder documented in .env.example,
// which is stricter. This matches on shape instead, because the bundle has no
// access to .env.example.

/** Placeholders in `.env.example` all read `your_<something>_here`. */
const PLACEHOLDER_PATTERN = /^your_.*_here$/i;

/**
 * Stand-in for the page origin when resolving a relative endpoint. Any value
 * that resolves off this origin would also resolve off the real one.
 */
const SAME_ORIGIN_SENTINEL = 'https://same-origin.invalid';

/**
 * Hosts where a plaintext `http:` endpoint is a development convenience rather
 * than a mistake. Everywhere else it is rejected - see {@link configuredEndpoint}.
 */
const PLAINTEXT_ALLOWED_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

/**
 * The normalised form of a `VITE_*` value, or `null` when it is blank or a
 * documented placeholder.
 *
 * Returns the value rather than a `value is string` predicate on purpose. The
 * predicate form narrowed `string` to `string`, so it bought no safety while
 * leaving the RAW value in scope after the guard - every call site then had to
 * remember to `.trim()` by hand, and one that forgot would ship a padded key
 * that had been validated in trimmed form. Handing back the trimmed value makes
 * it the only thing a caller can obtain.
 *
 * A placeholder is worse than an empty value: it makes every request fail at
 * the provider behind a generic error banner, while an empty one at least
 * routes the user to the direct-contact fallback.
 */
export function configuredValue(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0) return null;
  return PLACEHOLDER_PATTERN.test(trimmed) ? null : trimmed;
}

/**
 * The normalised form of a `VITE_*` endpoint, or `null` when it is not usable.
 *
 * Accepted: an absolute `https:` URL, a same-origin absolute path, and `http:`
 * only against a loopback host.
 *
 * Same-origin paths are accepted because routing the provider call through the
 * site's own serverless function is the natural wiring on this host - it is how
 * the provider key stays off the client.
 *
 * `http:` is otherwise rejected because this site is served over https, so a
 * plaintext endpoint has exactly two outcomes and both are bad: the browser
 * blocks it as mixed content and the reader sees a generic transient error
 * instead of the configuration mistake it is, or - anywhere the block does not
 * apply - subscriber email addresses go out in the clear. Loopback is exempt
 * because a local provider stub is a normal thing to point at while developing.
 *
 * What stays rejected is everything that merely looks configured: a
 * placeholder, a bare word, anything that resolves off-origin however it is
 * spelled (`//host` and the `/\host` variants that normalise to it silently
 * retarget the POST at a third party), and any other scheme. Those would render
 * a live form that posts subscriber addresses somewhere they are not read,
 * while the visitor sees only a generic error.
 */
export function configuredEndpoint(value: string | undefined): string | null {
  const trimmed = configuredValue(value);
  if (trimmed === null) return null;

  if (trimmed.startsWith('/')) {
    // A leading `/` is not enough to prove same-origin. `//evil.example` is
    // protocol-relative, and `/\evil.example` reaches the same place because
    // WHATWG parsing treats a backslash as a separator for http(s) - as does
    // `fetch()` when it resolves the value against the page origin. Stripped
    // tabs and newlines can rebuild `//` the same way. So resolve the value the
    // way the request will and keep only what stays on the base origin.
    //
    // `new URL(<leading-slash path>, <valid base>)` cannot throw, so there is
    // no catch here: an unreachable one could never be covered by a test.
    return new URL(trimmed, SAME_ORIGIN_SENTINEL).origin === SAME_ORIGIN_SENTINEL ? trimmed : null;
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  if (parsed.protocol === 'https:') return trimmed;
  if (parsed.protocol === 'http:' && PLAINTEXT_ALLOWED_HOSTNAMES.has(parsed.hostname)) {
    return trimmed;
  }
  return null;
}
